<template>
  <!--
    36協定の上限に触れている方。**経営管理者以上だけ。**

    ダッシュボードはログイン直後に必ず開く画面なので、幹部が自分から
    探しに行かなくても目に入る。**該当者が0名なら親がこのカードを
    描かない。**カードが出ていること自体が信号になる。

    出し分けは親が持ち、この部品は渡されたものを描くだけにする。
  -->
  <article class="preika-insight-card overtime-limit-card">
    <div class="preika-insight-heading">
      <div>
        <p class="preika-insight-heading__eyebrow">OVERTIME LIMIT</p>
        <h2>36協定の上限に触れている方</h2>
        <p class="preika-insight-heading__description">
          時間外・休日労働の上限の判定結果です。氏名と理由は専用画面で確認できます。
        </p>
      </div>
      <span v-if="targetMonthLabel" class="preika-insight-badge">
        {{ targetMonthLabel }}
      </span>
    </div>

    <ul class="overtime-limit-card__counts">
      <li
        v-for="item in counts"
        :key="item.key"
        :class="['overtime-limit-card__count', 'is-' + item.key]"
      >
        <span class="overtime-limit-card__count-value">{{ item.value }}</span>
        <span class="overtime-limit-card__count-label">{{ item.label }}</span>
      </li>
    </ul>

    <!--
      強い順に上位3名まで。残りは人数だけ。
      **全員をここへ並べない。**ダッシュボードは一覧の画面ではない。
    -->
    <ul class="overtime-limit-card__rows">
      <li v-for="row in rows" :key="row.userId" class="overtime-limit-card__row">
        <span class="overtime-limit-card__level" :class="'is-' + levelKey(row.level)">
          {{ levelLabel(row.level) }}
        </span>
        <span class="overtime-limit-card__name">
          <strong>{{ row.name || '氏名未登録' }}</strong>
          <small>{{ row.departmentName || '未所属' }}</small>
        </span>
        <!--
          **2系統を、どちらも出す。**

          先頭の1件だけを出していたので、「単月101時間到達」と
          「36協定の期間が終わっています」が同時に起きている人では、
          強いほうの101時間だけが出て**協定が切れていることが消えていた。**
          逆に、協定違反と本人通知が重なると本人通知が消えた。

          データは両方持っているので、出し分けるだけでよい。
          判定していない範囲も、区分に隠れるので別に出す。
        -->
        <span class="overtime-limit-card__reason">
          <span v-for="(line, index) in reasonsOf(row)" :key="index"
                class="overtime-limit-card__reason-line"
                :class="'is-' + line.kind">
            <small>{{ line.label }}</small>{{ line.text }}
          </span>
        </span>
      </li>
    </ul>

    <p v-if="remaining > 0" class="overtime-limit-card__remaining">
      ほか {{ remaining }} 名
    </p>

    <router-link to="/admin/insights/overtime-limits" class="preika-text-link">
      36協定アラートを開く<CIcon name="cil-arrow-right"/>
    </router-link>
  </article>
</template>

<script>
import {
  overtimeLimitLevelKey,
  overtimeLimitLevelLabel
} from '../../utils/overtimeLimitStatus'

export default {
  name: 'DashboardOvertimeLimit',

  props: {
    /** 専用画面と同じ口の応答。親が1回だけ取って渡す。 */
    status: { type: Object, default: () => ({}) }
  },

  computed: {
    targetMonthLabel () {
      const text = String(this.status.targetMonth || '')
      const matched = text.match(/^(\d{4})-(\d{1,2})$/)
      return matched ? matched[1] + '年' + Number(matched[2]) + '月' : text
    },

    /*
     * 人数はサーバが数えたものをそのまま出す。**画面で数え直さない。**
     * 同じ数え方を2か所に書くと、片方だけ直したときに食い違う。
     *
     * 0名の枠も残す。**「判定できなかった 0名」が消えると、
     * 見ていないことが見えなくなる。**
     */
    counts () {
      return [
        { key: 'violation', label: '上限を超過', value: this.count('violationCount') },
        { key: 'warning', label: '限度時間を超過', value: this.count('warningCount') },
        // **「本人へ通知 8名」だけだと、送ったと読まれる。**
        // PREIKA から本人へ送る仕組みはまだ無い。
        { key: 'obligation', label: '本人へ通知（未送信）', value: this.count('obligationCount') },
        { key: 'unknown', label: '判定できず', value: this.count('unknownCount') }
      ]
    },

    rows () {
      return Array.isArray(this.status.rows) ? this.status.rows : []
    },

    totalCount () {
      return Number(this.status.totalCount) || 0
    },

    /** 表に出していない人数。サーバの合計から、出した行を引く。 */
    remaining () {
      return Math.max(0, this.totalCount - this.rows.length)
    }
  },

  methods: {
    count (key) {
      return Number(this.status[key]) || 0
    },

    /*
     * 区分の名前と寄せ方は utils に置く。**このカードで書き直さない。**
     * 専用画面と言葉がずれると、同じ人が別の区分に見えることになる。
     */
    levelKey: overtimeLimitLevelKey,
    levelLabel: overtimeLimitLevelLabel,

    /** いちばん強い理由。messages はサーバが強い順に返す。 */
    /**
     * <b>2系統と「判定していない範囲」を、それぞれ1行ずつ。</b>
     *
     * <p>先頭の1件だけにすると、強い指摘が弱い指摘を消す。
     * 単月101時間と協定の期限切れが同時に起きている人では、101時間だけが
     * 出て<b>協定が切れていることが画面から消える。</b>直すところが
     * まったく違うので、どちらも要る。</p>
     *
     * <p>それぞれ先頭の1件に絞るのは、ここがダッシュボードのカードだから。
     * 全部は専用画面で見る。</p>
     */
    reasonsOf (row) {
      const lines = []
      const pick = list => (Array.isArray(list) && list.length ? list[0] : null)

      const absolute = pick(row && row.absoluteMessages)
      if (absolute) {
        lines.push({ kind: 'absolute', label: '法令上限・本人通知', text: absolute })
      }
      const agreement = pick(row && row.agreementMessages)
      if (agreement) {
        lines.push({ kind: 'agreement', label: '36協定', text: agreement })
      }
      const notJudged = pick(row && row.notJudgedMessages)
      if (notJudged) {
        lines.push({ kind: 'not-judged', label: '判定していない範囲', text: notJudged })
      }

      if (lines.length) {
        return lines
      }
      /*
       * **どちらの系統にも文言が無いときだけ、まとめた側を使う。**
       * 古いサーバは系統別を返さない。ここで空にすると、理由が1つも
       * 出ないカードになる。
       */
      const messages = row && Array.isArray(row.messages) ? row.messages : []
      return messages.length
        ? [{ kind: 'absolute', label: '', text: messages[0] }]
        : [{ kind: 'absolute', label: '', text: '理由が記録されていません' }]
    }
  }
}
</script>
